import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SAOverview from './components/SAOverview';
import SAAgencies from './components/SAAgencies';
import SAUsers from './components/SAUsers';
import SAPricingPlans from './components/SAPricingPlans';
import SAPlatformSettings from './components/SAPlatformSettings';
import { useTenant } from '../../context/TenantContext';
import { useSuperAdmin } from '../../hooks/useSuperAdmin';

const SA_DARK = '#0F172A';
const SA_ACCENT = '#6366F1';

type SATab = 'overview' | 'agencies' | 'users' | 'plans' | 'settings';

const TAB_LABELS: Record<SATab, string> = {
  overview: 'Platform Overview',
  agencies: 'Agency Management',
  users: 'User Management',
  plans: 'Pricing & Plans',
  settings: 'Platform Settings',
};

const TAB_SUBS: Record<SATab, string> = {
  overview: 'Real-time platform health and revenue metrics',
  agencies: 'Manage all tenant agencies across the platform',
  users: 'View and manage all users across every agency',
  plans: 'Configure pricing tiers and feature access',
  settings: 'Global platform configuration and behavior flags',
};

export default function SuperAdminPage() {
  const [activeTab, setActiveTab] = useState<SATab>('overview');
  const { currentUser, signOut } = useTenant();
  const navigate = useNavigate();

  const { tenants, users, stats, loading, refetch } = useSuperAdmin();

  const handleSignOut = async () => {
    await signOut();
    navigate('/sa-login');
  };

  const navItems: { id: SATab; label: string; icon: string; badge?: number }[] = [
    { id: 'overview', label: 'Overview', icon: 'ri-dashboard-3-line' },
    { id: 'agencies', label: 'Agencies', icon: 'ri-building-4-line', badge: stats.total_agencies || undefined },
    { id: 'users', label: 'Users', icon: 'ri-group-line', badge: stats.total_users || undefined },
    { id: 'plans', label: 'Pricing Plans', icon: 'ri-price-tag-3-line' },
    { id: 'settings', label: 'Platform Settings', icon: 'ri-settings-4-line' },
  ];

  const mrrDisplay = stats.total_mrr > 0
    ? stats.total_mrr >= 1000
      ? `${(stats.total_mrr / 1000).toFixed(1)}K`
      : `${stats.total_mrr}`
    : '0';

  return (
    <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">
      {/* Super Admin Sidebar */}
      <aside className="w-64 flex-shrink-0 flex flex-col h-full" style={{ backgroundColor: SA_DARK }}>
        {/* Brand Header */}
        <div className="px-5 py-5 border-b border-white/10">
          <div className="flex items-center gap-3 mb-1">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
              style={{ backgroundColor: SA_ACCENT }}
            >
              SA
            </div>
            <div>
              <p className="text-white font-bold text-sm">TravelCRM</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span className="text-xs" style={{ color: SA_ACCENT }}>
                  Super Admin Panel
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          <p
            className="text-xs font-semibold uppercase tracking-widest px-3 py-2"
            style={{ color: 'rgba(255,255,255,0.3)' }}
          >
            Management
          </p>
          {navItems.map((item) => {
            const active = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer text-left ${
                  active ? 'text-white' : 'text-white/50 hover:text-white/80 hover:bg-white/5'
                }`}
                style={active ? { backgroundColor: SA_ACCENT } : {}}
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-base`} />
                </div>
                <span className="flex-1">{item.label}</span>
                {item.badge !== undefined && !active && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full font-bold bg-white/10 text-white/60">
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Platform Stats Strip */}
        <div className="px-4 py-4 border-t border-white/10 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white/5 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-white">
                {loading ? '…' : stats.total_agencies}
              </p>
              <p className="text-xs text-white/40">Agencies</p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-white">
                {loading ? '…' : `${mrrDisplay}`}
              </p>
              <p className="text-xs text-white/40">MRR (AED)</p>
            </div>
          </div>

          {/* Exit to Agency */}
          <Link
            to="/dashboard"
            className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors cursor-pointer text-white/50 hover:text-white/80 hover:bg-white/5"
          >
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-arrow-left-circle-line text-base" />
            </div>
            Exit to Agency View
          </Link>

          {/* Admin User */}
          <div className="flex items-center gap-2.5 px-3 py-2">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
              style={{ backgroundColor: SA_ACCENT }}
            >
              {currentUser?.name
                ? currentUser.name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()
                : 'SA'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-white/80 truncate">
                {currentUser?.name || 'Super Admin'}
              </p>
              <p className="text-xs text-white/30 truncate">
                {currentUser?.email || 'nexcrm11@gmail.com'}
              </p>
            </div>
            <button
              onClick={handleSignOut}
              className="text-white/30 hover:text-white/70 transition-colors cursor-pointer"
              title="Sign out"
            >
              <i className="ri-logout-box-r-line text-sm" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="bg-white border-b border-slate-100 px-8 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h1 className="text-lg font-bold text-slate-800">{TAB_LABELS[activeTab]}</h1>
            <p className="text-xs text-slate-400 mt-0.5">{TAB_SUBS[activeTab]}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 rounded-full">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-xs text-emerald-700 font-semibold">All Systems Operational</span>
            </div>
            <div
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-white"
              style={{ backgroundColor: SA_ACCENT }}
            >
              <i className="ri-shield-check-line" />
              Super Admin
            </div>
            <button
              onClick={refetch}
              className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-slate-50 text-slate-400 cursor-pointer"
              title="Refresh data"
            >
              <i className={`ri-refresh-line text-lg ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </header>

        {/* Scrollable Content */}
        <main className="flex-1 overflow-y-auto p-8">
          {activeTab === 'overview' && (
            <SAOverview tenants={tenants} stats={stats} loading={loading} />
          )}
          {activeTab === 'agencies' && (
            <SAAgencies tenants={tenants} loading={loading} onRefetch={refetch} />
          )}
          {activeTab === 'users' && <SAUsers users={users} loading={loading} />}
          {activeTab === 'plans' && <SAPricingPlans />}
          {activeTab === 'settings' && <SAPlatformSettings />}
        </main>
      </div>
    </div>
  );
}
